import { constants } from "../context/constants";
import axiosInstance from "./axiosInstance";

const queryUrl = (path, values) => {
    const params = new URLSearchParams();
    Object.entries(values).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            params.append(key, String(value));
        }
    });
    const query = params.toString();
    return query ? `${path}?${query}` : path;
};

const pathSegment = (value) => encodeURIComponent(String(value ?? ''));

export const signUpUser = async ({ name, email, password }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.USER_SIGNUP}`,
            { name, email, password }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingRegister: false,
            alertModalShow: true,
        }
    } catch (error) {
        throw error;
    }
}

export const signInUser = async ({ email, password }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.USER_SIGNIN}`,
            { email, password }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingLogin: false,
            alertModalShow: true,
        }
    } catch (error) {
        throw error;
    }
}

export const getUser = async ({ headers }) => {
    try {
        const response = await axiosInstance.get(
            `${constants.USER_INFO}`,
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const setUser = async ({ name, email, headers }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.USER_INFO}`,
            { name, email },
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const setPassword = async ({ currentPassword, newPassword, headers }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.USER_SETPASSWORD}`,
            { currentPassword, newPassword },
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getCategories = async () => {
    try {
        const response = await axiosInstance.get(
            `${constants.GET_CATEGORIES}`
        );
        if (response.status === 200) return {
            data: response.data,
        }
    } catch (error) {
        throw error;
    }
}

export const getProductsByCategory = async ({ category_id, page }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(constants.GET_PRODUCTS_BY_CATEGORY, {
                category_id,
                page,
                visibleOnly: true
            }),
        )
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getProduct = async ({ _id, details = false }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(constants.GET_PRODUCT_BY_ID, {
                product: _id,
                ...(details ? { details: true } : {})
            }),
        )
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getAllProducts = async ({ page }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(constants.GET_ALL_PRODUCTS, { page, visibleOnly: true }),
        )
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getCartItems = async ({ items }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.GET_CART_PRODUCTS}`,
            { items }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

// A cart write rejected because the Server's cartRevision moved since this
// Client last read it. Distinguishing it from any other 4xx/5xx is what lets
// call sites show a specific message instead of a generic failure (D3-E will
// add refetch/merge/retry on top of this signal).
export const isCartRevisionConflict = (error) => (
    error?.response?.status === 409
    && error?.response?.data?.code === 'CART_REVISION_CONFLICT'
);

export const setCart = async ({ headers, newCart, expectedCartRevision }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.USER_UPDATE_CART}`,
            {
                newCart: newCart.map(({ _id, quantityToBuy }) => ({ _id, quantityToBuy })),
                expectedCartRevision
            },
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            cartRevision: response.data.cartRevision,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const deleteCartItem = async ({ headers, _id, expectedCartRevision }) => {
    try {
        const response = await axiosInstance.put(
            `${constants.DELETE_CART_ITEM}`,
            { _id, expectedCartRevision },
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            cartRevision: response.data.cartRevision,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const purchaseItems = async ({ headers, combos, idempotencyKey, reservationId }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.USER_PURCHASE}`,
            {
                combos: Array.isArray(combos) ? combos : [],
                idempotencyKey,
                ...(reservationId ? { reservationId } : {})
            },
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getMyMovements = async ({ headers, page, limit, search, status }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(constants.USER_MOVEMENTS, { page, limit, search, status }),
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getDistributorCredentialTemplate = async ({ headers, signal } = {}) => {
    const response = await axiosInstance.get(constants.DISTRIBUTOR_TEMPLATE, { headers, signal });
    return { data: response.data, loadingReq: false };
};

export const updateDistributorCredentialTemplate = async ({ headers, template }) => {
    const response = await axiosInstance.put(
        constants.DISTRIBUTOR_TEMPLATE,
        { template },
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const syncCartReservation = async ({ headers, combos }) => {
    const response = await axiosInstance.post(
        constants.CART_RESERVATION,
        { combos: Array.isArray(combos) ? combos : [] },
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const renewCartReservation = async ({ headers, combos }) => {
    const response = await axiosInstance.put(
        `${constants.CART_RESERVATION}/renew`,
        { combos: Array.isArray(combos) ? combos : [] },
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const getNotifications = async ({ headers, signal } = {}) => {
    const response = await axiosInstance.get(constants.USER_NOTIFICATIONS, { headers, signal });
    return { data: response.data, loadingReq: false };
};

export const markNotificationAsRead = async ({ id, headers }) => {
    const response = await axiosInstance.put(
        `${constants.USER_NOTIFICATIONS}/${pathSegment(id)}/read`,
        {},
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const markAllNotificationsAsRead = async ({ headers }) => {
    const response = await axiosInstance.put(
        `${constants.USER_NOTIFICATIONS}/read-all`,
        {},
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const getPushNotificationConfiguration = async ({ headers }) => {
    const response = await axiosInstance.get(
        `${constants.PUSH_NOTIFICATIONS}/config`,
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const registerPushNotificationSubscription = async ({ headers, subscription, audience = 'client' }) => {
    const response = await axiosInstance.put(
        `${constants.PUSH_NOTIFICATIONS}/subscription`,
        { audience, subscription },
        { headers }
    );
    return { data: response.data, loadingReq: false };
};

export const removePushNotificationSubscription = async ({ headers, endpoint }) => {
    const response = await axiosInstance.delete(
        `${constants.PUSH_NOTIFICATIONS}/subscription`,
        { headers, data: { endpoint } }
    );
    return { data: response.data, loadingReq: false };
};

// Public, read-only prebuilt-cart catalog (Sprint Combos 1.0C-B). Only page,
// limit and search are ever sent; undefined/null/empty values are omitted so
// the server's own defaults apply. Errors propagate untouched.
export const getStoreCombos = async ({ page, limit, search, headers } = {}) => {
    try {
        const params = new URLSearchParams();
        const query = { page, limit, search };
        Object.entries(query).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') params.append(key, value);
        });
        const queryString = params.toString();
        const url = queryString ? `${constants.STORE_COMBOS}?${queryString}` : constants.STORE_COMBOS;
        const response = headers
            ? await axiosInstance.get(url, { headers })
            : await axiosInstance.get(url);
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getStoreCombo = async ({ _id } = {}) => {
    try {
        const params = new URLSearchParams();
        if (_id !== undefined && _id !== null && _id !== '') params.append('_id', _id);
        const response = await axiosInstance.get(
            `${constants.STORE_COMBO}?${params.toString()}`
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getStoreTopSelling = async ({ headers } = {}) => {
    const response = headers
        ? await axiosInstance.get(constants.STORE_TOP_SELLING, { headers })
        : await axiosInstance.get(constants.STORE_TOP_SELLING);
    if (response.status === 200) return {
        data: response.data,
        loadingReq: false
    };
};

// The server responds 201 for a newly created request and 200 when the
// call exactly replays the caller's own still-pending request — unlike
// every other helper here, both are success, so this is the one call site
// that must not gate on status === 200 alone (a 201 would otherwise resolve
// to undefined and look like a silent failure).
export const createRechargeRequest = async ({ headers, amount, payerName, savePayer, setDefault }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.RECHARGE_REQUESTS_URL}`,
            { amount, payerName, savePayer, setDefault },
            { headers }
        );
        if (response.status === 200 || response.status === 201) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const isActiveRechargeRequest = (error) => (
    error?.response?.status === 409
    && error?.response?.data?.code === constants.ACTIVE_RECHARGE_REQUEST_CODE
);

export const getRechargeRequests = async ({ headers, signal }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(constants.RECHARGE_REQUESTS_URL, { _fresh: Date.now() }),
            { headers: { ...headers, 'Cache-Control': 'no-cache' }, signal }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getRechargeRequest = async ({ id, headers, signal }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(`${constants.RECHARGE_REQUESTS_URL}/${pathSegment(id)}`, { _fresh: Date.now() }),
            { headers: { ...headers, 'Cache-Control': 'no-cache' }, signal }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const cancelRechargeRequest = async ({ id, headers }) => {
    try {
        const response = await axiosInstance.post(
            `${constants.RECHARGE_REQUESTS_URL}/${pathSegment(id)}/cancel`,
            {},
            { headers }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const getRechargePayers = async ({ headers, signal }) => {
    try {
        const response = await axiosInstance.get(
            `${constants.RECHARGE_PAYERS_URL}`,
            { headers, signal }
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}

export const queryProducts = async ({ query }) => {
    try {
        const response = await axiosInstance.get(
            queryUrl(constants.QUERY_PRODUCTS, { productQuery: query }),
        );
        if (response.status === 200) return {
            data: response.data,
            loadingReq: false,
        }
    } catch (error) {
        throw error;
    }
}
